import * as React from 'react';
import { ScrollArea } from '@/shared/ui/scroll-area';
import { cn } from '@/shared/lib/cn';

export interface ConversationProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'dir'> {
  /** Rola pro fim sempre que o conteúdo muda (nova mensagem, texto chegando via stream). */
  autoScrollDeps?: unknown[];
}

/** Container rolável da conversa — rola pro fim automaticamente a cada nova mensagem/token. */
export const Conversation = React.forwardRef<HTMLDivElement, ConversationProps>(
  ({ className, children, autoScrollDeps = [], ...props }, forwardedRef) => {
    const rootRef = React.useRef<HTMLDivElement>(null);

    React.useImperativeHandle(forwardedRef, () => rootRef.current as HTMLDivElement);

    React.useEffect(() => {
      // O elemento que realmente rola é o Viewport interno do Radix ScrollArea,
      // não o Root (que o ref acima aponta) -- daí o querySelector.
      const viewport = rootRef.current?.querySelector<HTMLDivElement>('[data-radix-scroll-area-viewport]');
      if (!viewport) return;
      viewport.scrollTop = viewport.scrollHeight;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, autoScrollDeps);

    return (
      <ScrollArea ref={rootRef} className={cn('h-full', className)} {...props}>
        <div className="flex flex-col gap-4 p-4">{children}</div>
      </ScrollArea>
    );
  },
);
Conversation.displayName = 'Conversation';

export const ConversationEmptyState = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'flex flex-1 flex-col items-center justify-center gap-2 px-6 py-12 text-center text-sm text-muted-foreground',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  ),
);
ConversationEmptyState.displayName = 'ConversationEmptyState';
